import { getCallDownload } from "./api";
import type { CallDownload } from "./types";

const CANNOT_DOWNLOAD = "This meeting can't be downloaded.";
export const STILL_PROCESSING = "The recording is still processing — try again in a few minutes.";

/** How often the page asks whether the server has finished building the zip. */
export const POLL_MS = 3000;
/** The longest wait before giving up: a 1.5 GB call builds in well under a minute. */
const GIVE_UP_MS = 10 * 60 * 1000;
/** Failed requests in a row before the wait is abandoned. */
const MAX_FAILURES = 3;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Start the browser's own download of a link whose response is an attachment. */
function navigateTo(url: string, name: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Download this call as one zip, built on the server.
 * The first ask starts the build and answers "preparing", so this asks again
 * every few seconds, then hands the link to the browser, which streams the file
 * to disk. `failed` and `processing` end the wait at once with their own
 * message (the call can't be built, or its recording has not landed yet).
 * `onStarted` runs once the server has taken the request.
 */
export async function downloadCall(botId: string, onStarted?: () => void): Promise<void> {
  const deadline = Date.now() + GIVE_UP_MS;
  let failures = 0;
  let started = false;
  for (;;) {
    let download: CallDownload | null = null;
    try {
      download = await getCallDownload(botId);
      failures = 0;
    } catch {
      failures += 1;
      if (failures >= MAX_FAILURES) throw new Error(CANNOT_DOWNLOAD);
    }
    if (download) {
      if (!started) {
        started = true;
        onStarted?.();
      }
      if (download.status === "failed") throw new Error(CANNOT_DOWNLOAD);
      if (download.status === "processing") throw new Error(STILL_PROCESSING);
      if (download.status === "ready") {
        navigateTo(download.url, download.name);
        return;
      }
    }
    if (Date.now() >= deadline) throw new Error(CANNOT_DOWNLOAD);
    await sleep(POLL_MS);
  }
}
